// @section 1 Authenticated Fetch (Atlas)
// Adapted from OptiqOS authFetch — MSAL idToken only (no magic-link / prospect paths).

import { msalInstance, authReady } from "./msalInstance";
import { loginRequest } from "./msalConfig";

let cachedToken: string | null = null;
let cachedExpiry = 0;

// One-shot guard so an expired refresh token triggers exactly one interactive
// redirect per tab session — never a redirect loop if re-auth can't establish
// an AAD session. Cleared once a token is successfully acquired.
const REAUTH_FLAG = "atlas:reauth-redirect";

// @block 1.1 Prime the cache directly from a redirect result.
// The redirect IS yielding tokens (#code=… lands back on the app), but the old
// code only ever read acquireTokenSilent — whose hidden-iframe renewal is the
// thing failing — so a freshly-issued idToken was thrown away and every call
// 401'd while the one-shot guard suppressed any retry. AuthBoot calls this with
// handleRedirectPromise()'s result so the just-issued idToken is used directly.
export function primeToken(idToken: string, expiresOn?: Date | null) {
  if (!idToken) return;
  cachedToken = idToken;
  cachedExpiry = expiresOn?.getTime() ?? Date.now() + 3600_000;
  sessionStorage.removeItem(REAUTH_FLAG);
}

// @block 1.2 Full sign-out. Clears our reauth guard + cached token, then
// logoutRedirect (ends the AAD session, drops MSAL cache) → returns to "/"
// (postLogoutRedirectUri) → AuthBoot renders the sign-in screen. Also the
// one-click un-wedge if a session ever gets stuck authed-but-tokenless.
export async function signOut(): Promise<void> {
  cachedToken = null;
  cachedExpiry = 0;
  sessionStorage.removeItem(REAUTH_FLAG);
  const account = msalInstance.getAllAccounts()[0];
  await msalInstance.logoutRedirect(account ? { account } : undefined);
}

// @block 1.3 Acquire an idToken; on expired refresh token, re-auth interactively.
// MSAL's cached refresh token expires (telemetry: refresh_token_expired); when
// it does, acquireTokenSilent + the hidden-iframe fallback both fail and throw
// InteractionRequiredAuthError. The account record still exists, so the app
// renders — but every API call would 401 forever unless we force a real login.
//
// Exported so AuthBoot can prime the cache BEFORE mounting children. Children
// (MeProvider in particular) fire their /api/hq/me fetch from useEffect on
// first render; if the token isn't already cached, the request races the
// silent-iframe renewal and 401s. AuthBoot awaiting this on boot makes the
// "authed" state mean "token is in hand," not just "account record exists."
// @block 1.3a Single-flight. Screens like Reply Review fire 5+ authFetches at mount; when
// the cached token has just expired they ALL miss the cache and race into acquisition
// concurrently. That race is how a tab wedges (observed live 2026-07-31): the first racer
// sets REAUTH_FLAG and starts the redirect, a second racer's acquireTokenRedirect throws
// interaction_in_progress, and the tab is left with the flag set and no redirect pending —
// every later call returns null and 401s forever. One shared in-flight acquisition removes
// the race entirely; the flag-clear-on-redirect-failure below is the backstop.
let inflight: Promise<string | null> | null = null;

export function getAccessToken(): Promise<string | null> {
  if (cachedToken && Date.now() < cachedExpiry - 300_000) return Promise.resolve(cachedToken);
  if (inflight) return inflight;
  inflight = acquireToken().finally(() => { inflight = null; });
  return inflight;
}

async function acquireToken(): Promise<string | null> {
  try {
    await authReady;
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;
    const response = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account: accounts[0],
    });
    cachedToken = response.idToken;
    cachedExpiry = response.expiresOn?.getTime() ?? Date.now() + 3600_000;
    sessionStorage.removeItem(REAUTH_FLAG);
    return cachedToken;
  } catch {
    cachedToken = null;
    cachedExpiry = 0;

    // acquireTokenSilent failed with an account present. Silent renewal uses a
    // hidden iframe to login.microsoftonline.com, which fails as a TIMEOUT (not a
    // clean interaction_required) when third-party cookies are blocked or the AAD
    // refresh token has expired — previously the narrow interaction_required check
    // didn't match, so the app looped on silent iframes and 401'd every call
    // forever (authed-but-tokenless). Recover via a one-shot interactive redirect
    // (guarded by REAUTH_FLAG so it can never loop; cleared on the next successful
    // token). A full-page redirect runs in a first-party context, so it is immune
    // to the third-party-cookie block that breaks the iframe. AuthBoot's
    // handleRedirectPromise() + primeToken() complete it on return.
    if (!sessionStorage.getItem(REAUTH_FLAG)) {
      sessionStorage.setItem(REAUTH_FLAG, "1");
      try {
        const account = msalInstance.getAllAccounts()[0];
        await msalInstance.acquireTokenRedirect({
          scopes: loginRequest.scopes,
          ...(account ? { account } : {}),
        });
      } catch {
        // The redirect never left the page (e.g. interaction_in_progress). Leaving the
        // flag set here is the wedge: one-shot spent, no redirect pending, tokenless
        // forever. Clearing it lets the NEXT expiry attempt recovery again; the flag
        // still prevents a loop in the case it exists for, where the redirect DOES
        // navigate away and returns without a session.
        sessionStorage.removeItem(REAUTH_FLAG);
      }
    }
    return null;
  }
}

export async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  return fetch(url, { ...init, headers });
}
